import {
  DoorOptionsReducerTypes,
  SelfAssembleFields,
  useDoorOptionsReducer,
} from "app/hooks/";
import useDoorOptions from "app/hooks/configurator/useDoorOptions";
import { useScreenSize } from "app/hooks/shared/useScreenSize";
import {
  AllXLXXLDoorCodes,
  enabledXLXXLDeMaasDeWaalNewDoors,
} from "features/option-controls/door-width-controls/XLXXLHelper";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  ExtraOptionsType,
  OptionTemplate,
  OptionTemplatesByFilterResponse,
  Product,
} from "shared/api/middleware-api-client";
import {
  defaultDoorHeight,
  defaultDoorWidth,
} from "shared/utils/doorSceneConstants";
import { disableKozijnColor } from "shared/store/kozijn/kozijnStore";
import { Vector3 } from "three";
import { generateUUID } from "three/src/math/MathUtils";
import {
  deMaasCodes,
  deIjsselCodes,
  getClassicLayoutGroup,
  isDeMeentCode,
  getLayoutBrowseGroup,
  LayoutBrowseGroup,
  deWaalCodes,
  isCutLayoutCode,
  isDeIjsselCode,
  isDeMaasCode,
  isDeWaalLeafStyleLayoutCode,
  isDeWaalCode,
  isNewLayoutCode,
  type VerticalBeamPosition,
} from "entities/configurator/model/layoutMeta";
import { deMaasBeamInsetMm } from "entities/configurator/model/layoutGeometry";
import {
  defaultMinDoorWidth,
  defaultMinPanelWidth,
  getStaanderMinWidth,
  getStandingLayoutMinWidth,
  newLayoutMinWidth,
} from "entities/configurator/model/widthLimits";
import {
  getStaanderVerticalBeams,
  getVerticalProfileExtraCode,
  isVerticalProfileExtra,
  parseStaander,
  resolveSceneStaanderPosition,
  staanderPositions,
  supportsStaander,
} from "entities/configurator/model/staander";
import { getExtraOptionDisplayName } from "entities/configurator/model/extraOptionDisplay";
import {
  zakelijkDoorCodes,
  zakelijkProfielCodes,
} from "../../features/option-controls/door-width-controls/ZakelijkHelper";
import { maxHeightHandles } from "../../features/option-controls/eigenwijs-option-controls/index";
import {
  ConfiguredOptionPrices,
  ConfiguredOptionPricingInput,
  getConfiguredOptionPrices as computeConfiguredOptionPrices,
} from "./configuredOptionPricing";
import {
  BeamData,
  EigenwijsOptions,
  LayoutOptions,
  SizeObject,
} from "./DoorOptionsProvider.types";
import { normalizeInitialHandle } from "./handleNormalization";
import { getHandleAnchorBeamGap } from "./handlePolicy";
import {
  buildInitialLayoutEigenwijs,
  buildInitialSizeObjects,
  getDefaultLayoutBeamData,
  updateAnchorBeams,
} from "./layoutInitialization";
import { findMainVariant } from "./LayoutsHelper";
import { deMeentHeightMinDoors, minHeightDeMeent } from "./ZelfMontageHelper";
import { useDoorHeightNormalization } from "./useDoorHeightNormalization";
import { useHandlePolicyRepair } from "./useHandlePolicyRepair";
import { useUrlSelectionSync } from "./useUrlSelectionSync";
import { requiresEqualWidths } from "entities/configurator/model/widthSymmetry";
import {
  NEW_LAYOUT_OPTIONS_POSITIONS,
  LAYOUT_OPTIONS_POSITIONS,
} from "./layoutListOrder";

export const XLXXLDoors = ["schuifdeur", "taatsdeur", "paneel"];

export type DoorProduct = {
  totalPrice: number;
  doorName?: string;
  doorPrice?: string;
  doorLayoutName?: string;
  doorLayoutPrice?: string;
  doorColorName?: string;
  doorColorPrice?: string;
  doorHandleName?: string;
  doorHandlePrice?: string;
  doorGlassName?: string;
  doorGlassPrice?: string;
  montageText?: string;
  zelfMontagePrice?: string;
  isSelfAssembly: boolean;
  extraOptions?: ExtraOptionsType[];
};
export type ConfiguredProduct = Product & {
  eigenwijsLayout?: {
    code: string;
  };
  extraOptionsPrices?: number[];
};

export type ExtraOptionScheme = ExtraOptionsType & {
  name?: string;
  price?: number;
};
interface OptionContextProps {
  sortedLayoutList: OptionTemplate[];
  dewaalVariants: OptionTemplate[];
  deMaasVariants: OptionTemplate[];
  groupedLayouts: OptionTemplate[] | null;
  getLayoutSubchoiceCount: (code: string) => number;
  openLayoutGroup: (code: string) => void;
  closeLayoutGroup: () => void;
  doorOptions: OptionTemplatesByFilterResponse | undefined;
  doorOptionsLoading: boolean;
  doorOptionsLoadError: boolean;

  layout: OptionTemplate | null;
  eigenwijs: EigenwijsOptions | null;
  eigenwijs_layout?: string | null;
  staanderPosition: VerticalBeamPosition | undefined;
  sceneStaanderPosition: VerticalBeamPosition | undefined;
  staanderOption: OptionTemplate | undefined;
  color: OptionTemplate | null;
  glass: OptionTemplate | null;
  handle: OptionTemplate | null;
  extra: OptionTemplate[] | null;
  selectedDoor: OptionTemplate | null;
  minBeamCount: number;
  minDoorWidth: number;
  maxDoorWidth: number;
  minDoorHeight: number;
  maxDoorHeight: number;

  showBeamHeightValidationError: boolean;
  updateBeamHeightValidationError: (isError: boolean) => void;

  selfAssembleDoorData: SelfAssembleFields;
  updateSelfAssembleData: (data: SelfAssembleFields) => void;

  updateSelectedDoor: (door: OptionTemplate) => void;
  updateLayout: (layout: OptionTemplate) => void;
  updateColor: (color: OptionTemplate) => void;
  updateColoredGlass: (color: OptionTemplate) => void;
  updateGlass: (glass: OptionTemplate) => void;
  updateHandle: (handle: OptionTemplate) => void;
  updateExtra: (extra: OptionTemplate[]) => void;
  updateEigenwijs: (
    eigenwijs: EigenwijsOptions,
    layoutCode?: string,
    hasStaander?: boolean,
  ) => void;
  markPendingHeightRepairNotification: () => void;
  updateEigenwijsLayout: (eigenwijs_layout: string) => void;
  updateStaander: (position: VerticalBeamPosition | undefined) => void;
  updateSearchParam: (param: string, value: string) => void;

  calculateNewBeamHeight: (beams: BeamData[], customHeight: number) => number;
  updateAnchorBeams: (doorHeight: number, beams: BeamData[]) => BeamData[];

  getDefaultLayoutBeamData: (
    doorHeight: number,
    beams: BeamData[],
  ) => BeamData[];

  getConfigurationBeams: (ignoreAnchors: boolean) => BeamData[];
  getConfigurationAnchorBeams: () => BeamData[];
  getConfigurationHeight: (index: number) => number;
  getConfigurationWidth: (index: number) => number;
  getConfigurationPositions: (defaultPositions: Vector3[]) => Vector3[];
  getConfigurationColor: () => string;
  getConfigurationGlass: () => string;
  getConfiguredOptionPrices: () => ConfiguredOptionPrices;
  getConfigurationPrice: () => number;
  getConfigurationProduct: () => Product | null;
  getDoorProduct: () => DoorProduct;
  hasXLDoorOption: (extras: OptionTemplate[]) => boolean;
  hasXXLDoorOption: (extras: OptionTemplate[]) => boolean;
  resetVerfsetExtraOptions: () => void;
  isExtraChecked: (
    extra: OptionTemplate[] | null,
    item: OptionTemplate,
  ) => boolean;

  isSelfAssembly: boolean;
  isDoubleJHandle: boolean;
  isDoubleHandleChosen: boolean;

  isDeMaasLayout: boolean;
  isDeMaas2DubbelLayout: boolean;
  isDeWaalLayout: boolean;
  isDeIjsselLayout: boolean;
  isNotMaasWaalIjsselLayout: boolean;
  isCutLayout: boolean;
  mainDeWaal: OptionTemplate | null;
  mainDeMaas: OptionTemplate | null;

  updateMaxDoorWidth: (width: number) => void;
  doorLayoutOptions: OptionTemplate[];
  calculateExtrasPrice: () => number;

  isXLXXLScene: boolean;
  deMeentHeightChangeApplies: boolean;
  rotateDeWaal23: boolean;
  rotateDeWaal4: boolean;
  rotateDeMaas2Dubbel: boolean;
  isOpdekOrStomp: boolean;

  isNewLayout: boolean;
  isSingleMaxHeightDoor: boolean;
  isLeafStyleDeWaalLayout: boolean;
}

export const newLayoutDoors = [
  "taatsdeur",
  "schuifdeur",
  "scharnierdeur",
  "dubbel",
  "dubbeleschuifdeur",
  "dubbelescharnierdeur",
  "paneel",
  "paneelpaneel",
  "schuifdeur1paneel",
  "taatsdeur1paneel",
  "scharnierdeur1paneel",
  "dubbeleschuifdeur2paneel",
  "dubbelescharnierdeur2paneel",
  "dubbel2paneel",
];

export const doorCodeXLMin: number = 1151;
export const doorCodeXLMax: number = 1450;
export const doorCodeXL: string = doorCodeXLMin + "-" + doorCodeXLMax;
export const doorCodeXXLMin: number = 1451;
export const doorCodeXXLMax: number = 1750;
export const doorCodeXXL: string = doorCodeXXLMin + "-" + doorCodeXXLMax;

export const kaderdeurSelectableWidths = [880, 930, 980, 1030];
export const kaderdeurSelectableHeights = [2115, 2315];
export const kaderdeurDoorPositions = ["Loopdeur", "Vastedeur"];

const defaultEigenwijsLayout = "populair_1";

const layoutOptions: LayoutOptions[] = [
  {
    name: "deblaak",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 2100,
          adjustable: false,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: 853,
          adjustable: false,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "demeent",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 1920,
          adjustable: false,
          handleAnchor: false,
        },
        {
          id: generateUUID(),
          height: 1280,
          adjustable: false,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: 640,
          adjustable: false,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dekaap",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 1150,
          adjustable: false,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: 850,
          adjustable: false,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dekaapdewaal",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 1150,
          adjustable: false,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: 850,
          adjustable: false,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "decooldewaal",
    options: {
      selectedBeam: null,

      beams: [
        // first beam: 2560 - 300, but updates dynamically to the height
        {
          id: generateUUID(),
          height: 2260,
          adjustable: true,
          handleAnchor: false,
        },
        //1180
        {
          id: generateUUID(),
          height: 1180,
          adjustable: true,
          handleAnchor: true,
        },
        //880
        {
          id: generateUUID(),
          height: 880,
          adjustable: true,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 3000, customWidth: 920 }],
    },
  },
  {
    name: "debeursdewaal",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 2160,
          adjustable: true,
          handleAnchor: true,
        },

        //400 mm from the floor
        {
          id: generateUUID(),
          height: 400,
          adjustable: true,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "debeursdewaalcut",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 2160,
          adjustable: true,
          handleAnchor: true,
        },

        //400 mm from the floor
        {
          id: generateUUID(),
          height: 400,
          adjustable: true,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dekaapdewaaldubbel",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 1150,
          adjustable: false,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: 850,
          adjustable: false,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "decooldewaaldubbel",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 2260,
          adjustable: true,
          handleAnchor: false,
        },
        {
          id: generateUUID(),
          height: 1180,
          adjustable: true,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: 880,
          adjustable: true,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 3000, customWidth: 920 }],
    },
  },
  {
    name: "debeursdewaaldubbel",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 2160,
          adjustable: true,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: 400,
          adjustable: true,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "debeursdewaalcutdubbel",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 2160,
          adjustable: true,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: 400,
          adjustable: true,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "debeursdemaas",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 2560 - deMaasBeamInsetMm,
          adjustable: true,
          handleAnchor: true,
        },

        {
          id: generateUUID(),
          height: deMaasBeamInsetMm,
          adjustable: true,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "debeursdemaasdubbel",
    options: {
      selectedBeam: null,
      beams: [
        {
          id: generateUUID(),
          height: 2560 - deMaasBeamInsetMm,
          adjustable: true,
          handleAnchor: true,
        },
        {
          id: generateUUID(),
          height: deMaasBeamInsetMm,
          adjustable: true,
          handleAnchor: true,
        },
      ],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "demaas",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "demaasdubbel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "demaas2dubbel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dewaal",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dewaal2",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dewaal2dubbel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dewaal3dubbel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dewaal4dubbel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "dewaaldubbel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "deijssel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "deijssel1",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "deijsseldubbel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "deijssel1dubbel",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "geenliggerszakelijk",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
  },
  {
    name: "eigenwijs",
    options: {
      selectedBeam: null,
      beams: [],
      sizeObjects: [{ customHeight: 2560, customWidth: 920 }],
    },
    eigenwijs_layouts: {
      populair_1: {
        //DE BEURS
        beams: [
          //400 mm from the top ( updates dynamically )
          {
            id: generateUUID(),
            height: 2160,
            adjustable: true,
            handleAnchor: true,
          },

          //400 mm from the floor
          {
            id: generateUUID(),
            height: 400,
            adjustable: true,
            handleAnchor: true,
          },
        ],
      },
      populair_2: {
        //COOL
        beams: [
          // first beam: 2560 - 300, but updates dynamically to the height
          {
            id: generateUUID(),
            height: 2260,
            adjustable: true,
            handleAnchor: false,
          },
          //1180
          {
            id: generateUUID(),
            height: 1180,
            adjustable: true,
            handleAnchor: true,
          },

          //880
          {
            id: generateUUID(),
            height: 880,
            adjustable: true,
            handleAnchor: true,
          },
        ],
      },
      zelfkiezen: {
        // fully customizable
        beams: [],
      },
    },
  },
];

const ConfigurationOptionsContext = createContext<OptionContextProps>(
  {} as OptionContextProps,
);

export const useConfigurationOptionsContext = () =>
  useContext(ConfigurationOptionsContext);
// TODO: Extract all logic related to door options to hooks,
// and combine this provider with StandardProductProvider in ConfigurationOptionsProvider.
// That way they would only be used as stores
const ConfigurationOptionsProvider = ({ children }: any) => {
  // URL params
  const [searchParams, setSearchParams] = useSearchParams();
  const searchedDoor = searchParams.get("door") || "";
  const searchedLayout = searchParams.get("layout") || "";
  const searchedEigenwijsLayout = searchParams.get("eigenwijs_layout") || "";
  const searchedStaander = searchParams.get("staander");
  const searchedColor = searchParams.get("color") || "";
  const searchedGlass = searchParams.get("glass") || "";
  const searchedHandle = searchParams.get("handle") || "";
  const searchedExtra = searchParams.get("extra") || "";

  const searchedHeight = searchParams.get("height") || "";
  const searchedWidth = searchParams.get("width") || "";
  const searchedBeam = searchParams.get("beam") || "";
  const searchedIsSelfAssembly = searchParams.get("isSelfAssembly");

  const { state, dispatch } = useDoorOptionsReducer();
  const [layoutNavigation, setLayoutNavigation] = useState<{
    doorCode: string | undefined;
    layoutCode: string | undefined;
    group: LayoutBrowseGroup | null;
  } | null>(null);
  const initialHandleNormalizationDone = useRef(false);
  const pendingHeightRepairNotificationRef = useRef(false);
  const { isMobileScreen } = useScreenSize();
  const handleCode = state.handle?.code || "";

  const sortableLayouts =
    newLayoutDoors.includes(state.selectedDoor?.code) && !isMobileScreen
      ? NEW_LAYOUT_OPTIONS_POSITIONS
      : LAYOUT_OPTIONS_POSITIONS;

  // Options data
  const { doorOptions, doorOptionsLoading, doorOptionsLoadError } =
    useDoorOptions(
      {
        door: searchedDoor,
        chosenOptions: [
          searchedLayout,
          searchedEigenwijsLayout,
          searchedColor,
          searchedGlass,
          searchedHandle,
        ].filter(Boolean),
      },
      sortableLayouts,
    );

  const profileOption: OptionTemplate | undefined = doorOptions?.extra.find(
    (option: OptionTemplate) =>
      option.code === getVerticalProfileExtraCode(searchedDoor),
  );
  const requestedStaander = parseStaander(searchedStaander);
  const selectedPreset =
    state.layout?.code === "eigenwijs"
      ? state.eigenwijs_layout ||
        searchedEigenwijsLayout ||
        defaultEigenwijsLayout
      : undefined;
  const supportsSelectedStaander = supportsStaander(
    state.layout?.code,
    selectedPreset,
    state.selectedDoor?.code,
  );
  const canSelectStaander = supportsSelectedStaander && !!profileOption;
  const hasStaanderInUrl =
    !!profileOption &&
    supportsStaander(
      searchedLayout,
      searchedEigenwijsLayout || defaultEigenwijsLayout,
      searchedDoor,
    ) &&
    requestedStaander !== undefined;
  const staanderPosition = canSelectStaander ? requestedStaander : undefined;
  const sceneStaanderPosition = resolveSceneStaanderPosition({
    isScreenshot: searchParams.get("screenshot") === "true",
    doorCode: state.selectedDoor?.code,
    layoutCode: state.layout?.code,
    presetCode: selectedPreset,
    requestedPosition: requestedStaander,
    selectedPosition: staanderPosition,
  });
  const staanderOption = canSelectStaander ? profileOption : undefined;
  const selectedExtras: OptionTemplate[] =
    staanderPosition && staanderOption
      ? [...(state.extra ?? []), staanderOption]
      : (state.extra ?? []);

  // Determine if DeMeent layout height check applies
  const deMeentHeightChangeApplies =
    state.selectedDoor &&
    deMeentHeightMinDoors.includes(state.selectedDoor.code);

  // * Zelf montage min height should be 2315 if:
  // ?  Layout is demeent - DOORS:
  // * taatsdeur, opdekdeur, schuifdeur ( zelfmontage )
  // * stompedeur, scharnierdeur
  const zelfMontageMinHeightApplies =
    state.layout &&
    isDeMeentCode(state.layout.code) &&
    deMeentHeightChangeApplies;

  // Check if the selected door is an opdek or stomp door
  const isOpdekOrStomp =
    state.selectedDoor?.code.includes("opdekdeur") ||
    state.selectedDoor?.code === "stompedeur";

  const isSingleMaxHeightDoor =
    isOpdekOrStomp || state.selectedDoor?.code === "scharnierdeur";

  // Update the door using searched door from url params
  useEffect(() => {
    if (
      doorOptionsLoading ||
      doorOptionsLoadError ||
      !doorOptions ||
      // Only run this effect for doors
      !searchedDoor
    )
      return;

    const selectedDoor = doorOptions.door[0];

    if (selectedDoor && state.selectedDoor?.code !== selectedDoor.code) {
      dispatch({
        type: DoorOptionsReducerTypes.UPDATE_SELECTED_DOOR,
        data: selectedDoor,
      });
    }

    updateMinDoorWidth(defaultMinDoorWidth);
    updateMaxDoorWidth(1150);
    updateMinDoorHeight(1900);

    if (zelfMontageMinHeightApplies) {
      updateMinDoorHeight(minHeightDeMeent);
    }

    if (
      AllXLXXLDoorCodes.includes(searchedDoor) &&
      state.layout &&
      (!isNewLayoutCode(state.layout.code) ||
        enabledXLXXLDeMaasDeWaalNewDoors.includes(searchedDoor))
    ) {
      updateMaxDoorWidth(1750);
    }
    if (searchedDoor === "opdekdeurmetkozijn" || searchedDoor === "opdekdeur") {
      if (state.selfAssembleDoorData.selfAssembly) {
        updateMaxDoorHeight(2360);
      } else {
        updateMaxDoorHeight(2700);
      }
    } else {
      updateMaxDoorHeight(3000);
    }

    if (state.layout && isNewLayout) {
      updateMinDoorHeight(2315);
      if (isSingleMaxHeightDoor) {
        updateMaxDoorHeight(2700);
      }
    }
  }, [state.layout?.code, searchParams, doorOptions?.door, searchedDoor]);

  // Syncronize selfassemblydata with query parameters
  useEffect(() => {
    if (searchedDoor) {
      //Grab the query parameter if it exists
      let isSelfAssembly = searchedIsSelfAssembly === "true";

      // If its a business door, isselfasembly is always true
      if (zakelijkDoorCodes.includes(searchedDoor)) {
        isSelfAssembly = true;
      }

      //Update params+data
      updateSearchParam("isSelfAssembly", isSelfAssembly.toString());

      updateSelfAssembleData({
        ...state.selfAssembleDoorData,
        selfAssembly: isSelfAssembly,
      });
    }
  }, []);

  useEffect(() => {
    if (state.layout && state.selectedDoor) {
      const initialEigenwijs = buildInitialLayoutEigenwijs({
        createId: generateUUID,
        doorCode: state.selectedDoor.code,
        elementCount: state.selectedDoor.configuration.nElements || 0,
        layoutCode: state.layout.code,
        layoutOptions,
        searchedBeam,
        searchedHeight,
        searchedWidth,
        selfAssembly: state.selfAssembleDoorData.selfAssembly,
      });

      if (initialEigenwijs) {
        updateEigenwijs(initialEigenwijs, state.layout.code, hasStaanderInUrl);
      }
    }
  }, [state.selectedDoor, state.layout]);

  useEffect(() => {
    // Check the different size and determine door name and extra option
    if (
      !state.eigenwijs?.sizeObjects ||
      !state.selectedDoor ||
      !AllXLXXLDoorCodes.includes(state.selectedDoor?.code)
    )
      return;

    const selectedWidth = state.eigenwijs?.sizeObjects[0].customWidth;
    const isSelectedDoorXL =
      selectedWidth >= doorCodeXLMin && selectedWidth <= doorCodeXLMax;
    const isSelectedDoorXXL =
      selectedWidth >= doorCodeXXLMin && selectedWidth <= doorCodeXXLMax;

    // Replace the selected door name to the initial name
    state.selectedDoor.name = state.selectedDoor.name.replace(" XL", "");
    state.selectedDoor.name = state.selectedDoor.name.replace(" XXL", "");

    // Set the new door name and option that is selected
    if (isSelectedDoorXL)
      state.selectedDoor.name = state.selectedDoor.name + " XL";
    else if (isSelectedDoorXXL)
      state.selectedDoor.name = state.selectedDoor.name + " XXL";

    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_SELECTED_DOOR,
      data: state.selectedDoor,
    });
  }, [state.eigenwijs?.sizeObjects]);

  useEffect(() => {
    if (!searchedExtra && state.eigenwijs && doorOptions?.extra) {
      // Get the width of the door
      const currentWidth = state.eigenwijs.sizeObjects[0].customWidth;

      // Get the extra option setups for XL - XXL doors
      const extraDoorCodeXL = doorOptions.extra.filter(
        (item: OptionTemplate, id) => item.code.includes(doorCodeXL),
      );
      const extraDoorCodeXXL = doorOptions.extra.filter(
        (item: OptionTemplate, id) => item.code.includes(doorCodeXXL),
      );

      // If the width falls in the xl or xxl category -> update the extra options
      if (currentWidth >= doorCodeXLMin && currentWidth <= doorCodeXLMax) {
        updateExtra(extraDoorCodeXL);
      } else if (
        currentWidth >= doorCodeXXLMin &&
        currentWidth <= doorCodeXXLMax
      ) {
        updateExtra(extraDoorCodeXXL);
      }
    }
  }, [searchedExtra, state.eigenwijs]);

  useEffect(() => {
    if (
      !state.eigenwijs ||
      !state.eigenwijs.sizeObjects ||
      !doorOptions ||
      !doorOptions.extra
    )
      return;

    const extraDoorCodeXL = doorOptions?.extra.filter(
      (item: OptionTemplate, id) => item.code.includes(doorCodeXL),
    );
    const extraDoorCodeXXL = doorOptions?.extra.filter(
      (item: OptionTemplate, id) => item.code.includes(doorCodeXXL),
    );
    if (!extraDoorCodeXL || !extraDoorCodeXXL) {
      return;
    }
    let newExtraOptions = state.extra || [];
    const currentWidth = state.eigenwijs.sizeObjects[0].customWidth;
    const hasDoorCodeXL = !!state.extra?.some((item: OptionTemplate, id: any) =>
      item.code.includes(doorCodeXL),
    );
    const hasDoorCodeXXL = !!state.extra?.some(
      (item: OptionTemplate, id: any) => item.code.includes(doorCodeXXL),
    );

    if (currentWidth >= doorCodeXLMin && currentWidth <= doorCodeXLMax) {
      if (!hasDoorCodeXL) {
        newExtraOptions.push(extraDoorCodeXL[0]);
      }
      if (hasDoorCodeXXL) {
        newExtraOptions = newExtraOptions.filter(
          (option: OptionTemplate) => !option.code.includes(doorCodeXXL),
        );
      }
    } else if (
      currentWidth >= doorCodeXXLMin &&
      currentWidth <= doorCodeXXLMax
    ) {
      if (hasDoorCodeXL) {
        newExtraOptions = newExtraOptions.filter(
          (option: OptionTemplate) => !option.code.includes(doorCodeXL),
        );
      }
      if (!hasDoorCodeXXL) {
        newExtraOptions.push(extraDoorCodeXXL[0]);
      }
    } else {
      newExtraOptions = newExtraOptions.filter(
        (option: OptionTemplate) =>
          !option.code.includes(doorCodeXL) &&
          !option.code.includes(doorCodeXXL),
      );
    }

    updateExtra(newExtraOptions);
  }, [state.eigenwijs?.sizeObjects]);

  const updateBeamCount = useCallback(() => {
    if (state.eigenwijs) {
      const allMinBeamCount = [
        state.selectedDoor?.minBeamCount,
        state.color?.minBeamCount,
        state.glass?.minBeamCount,
        state.handle?.minBeamCount,
      ];
      const max = Math.max(...allMinBeamCount.map((c) => c ?? 0));

      dispatch({
        type: DoorOptionsReducerTypes.UPDATE_MIN_BEAM_COUNT,
        data: max,
      });
    }
  }, [
    state.eigenwijs,
    state.selectedDoor?.minBeamCount,
    state.color?.minBeamCount,
    state.glass?.minBeamCount,
    state.handle?.minBeamCount,
    dispatch,
  ]);

  /* This is a React hook that is called when the door, eigenwijs, color, glass, or handle changes. */
  useEffect(() => {
    updateBeamCount();
  }, [
    updateBeamCount,
    state.selectedDoor,
    state.color,
    state.glass,
    state.handle,
    state.eigenwijs,
  ]);

  useEffect(() => {
    // If layout hasn't loaded yet, return early
    if (
      !state.layout ||
      !state.selectedDoor ||
      state.layout.code !== searchedLayout
    )
      return;

    if (state.layout.code === "eigenwijs") {
      // If we are on Eigenwijs,
      // set the eigenwijs_layout to the one from the URL or default it (populair_1)
      const preset = searchedEigenwijsLayout || defaultEigenwijsLayout;
      if (state.eigenwijs_layout !== preset) {
        dispatch({
          type: DoorOptionsReducerTypes.UPDATE_EIGENWIJS_LAYOUT,
          data: preset,
        });
      }

      addDefaultBeams(preset, state.minBeamCount, state.selectedDoor);
    } else {
      // If layout is changing from eigenwijs to anything else,
      // reset the eigenwijs_layout
      if (state.eigenwijs_layout !== null) {
        dispatch({
          type: DoorOptionsReducerTypes.UPDATE_EIGENWIJS_LAYOUT,
          data: null,
        });
      }
    }
  }, [
    state.layout?.code,
    state.selectedDoor?.code,
    searchedEigenwijsLayout,
    searchedLayout,
  ]);

  const updateColoredGlass = (color: OptionTemplate) => {
    if (state.glass && color && doorOptions && doorOptions.glass) {
      if (state.glass.code.includes("alu")) {
        const { glass } = doorOptions;

        const coloredGlass = glass.find(
          (glassItem) =>
            glassItem.material?.codeName === color.material?.codeName,
        ) as OptionTemplate;

        updateGlass(coloredGlass);
      }
    }
  };

  const updateEigenwijsLayout = (eigenwijs_layout: string) => {
    searchParams.set("eigenwijs_layout", eigenwijs_layout);
    if (
      !supportsStaander(
        state.layout?.code,
        eigenwijs_layout,
        state.selectedDoor?.code,
      )
    ) {
      searchParams.delete("staander");
    }
    setSearchParams(searchParams, { replace: true });
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_EIGENWIJS_LAYOUT,
      data: eigenwijs_layout,
    });
  };

  const updateStaander = (position: VerticalBeamPosition | undefined) => {
    if (position === undefined) {
      deleteSearchParam("staander");
      return;
    }

    if (state.eigenwijs && state.selectedDoor) {
      const minimumWidth = getStaanderMinWidth(state.selectedDoor.code);
      const hasUndersizedLeaf = state.eigenwijs.sizeObjects.some(
        ({ customWidth }: SizeObject) => customWidth < minimumWidth,
      );
      const nextEigenwijs = hasUndersizedLeaf
        ? {
            ...state.eigenwijs,
            sizeObjects: state.eigenwijs.sizeObjects.map(
              (sizeObject: SizeObject) => ({
                ...sizeObject,
                customWidth: Math.max(sizeObject.customWidth, minimumWidth),
              }),
            ),
          }
        : state.eigenwijs;
      updateEigenwijs(nextEigenwijs, state.layout?.code, true);
    }

    updateSearchParam("staander", position);
  };

  /**
   * If the number of beams is less than the minimum number of beams, add the minimum number of beams
   */
  const addDefaultBeams = (
    eigenwijsLayout: string,
    minBeamCount: number,
    selectedDoor: OptionTemplate,
  ) => {
    // Retrieve height and width from query parameters, or default to 2560 and 920
    const customHeight = searchedHeight ? parseInt(searchedHeight) : 2560;

    const sizeObjects = buildInitialSizeObjects({
      doorCode: selectedDoor.code,
      elementCount: selectedDoor.configuration.nElements || 0,
      height: customHeight,
      searchedWidth,
    });

    // Set newEigenwijs object with the selected beam, sizeObjects, and beams from the configuration
    const newEigenwijs: EigenwijsOptions = {
      selectedBeam: state?.eigenwijs?.selectedBeam,
      sizeObjects,
      // Assign the configuration beams to the newEigenwijs object
      beams: [],
    };

    // Dequery the beam query parameters from the URL
    const beamQueryParameterValues = decodeURIComponent(searchedBeam);
    const beamQueryParameters = beamQueryParameterValues
      ? JSON.parse(beamQueryParameterValues)
      : [];

    // If we have query beam parameters, use those for the beam heights
    if (
      beamQueryParameters &&
      // and only change the layout if the beam count is greater than the minimum beam count
      beamQueryParameters.length > minBeamCount &&
      //if the eigenwijs_layout is different from the searched eigenwijs_layout
      // USE-CASES: when refreshing url OR going from non eigenwijs to eigenwijs (e.g. deblaak -> eigenwijs)
      state.eigenwijs_layout !== searchedEigenwijsLayout
    ) {
      // Only change if the values differ
      newEigenwijs.beams = beamQueryParameters.map((beamHeight: number) => {
        return {
          id: generateUUID(),
          height: beamHeight,
          adjustable: true,
          handleAnchor: true,
        };
      });
    }

    // Else add defaults from configuration
    else {
      const eigenwijsLayoutOption = layoutOptions.find(
        (option) => option.name === "eigenwijs",
      );

      if (eigenwijsLayoutOption) {
        newEigenwijs.beams = [
          ...eigenwijsLayoutOption.eigenwijs_layouts[eigenwijsLayout].beams,
        ];
      }

      //Update the first beam of the custom eigenwijs' depending on the height
      switch (eigenwijsLayout) {
        case "populair_1":
          const searchedHeightInt = parseInt(searchedHeight ?? "2560");
          let height = searchedHeightInt;

          if (
            maxHeightHandles.includes(handleCode) &&
            searchedHeightInt > 2900
          ) {
            height = 2900;
          }
          newEigenwijs.beams[0].height = height - 400;
          break;
        case "populair_2":
          newEigenwijs.beams[0].height =
            parseInt(searchedHeight ?? "2560") - 300;
          break;
        case "zelfkiezen":
          for (let i = 0; i < minBeamCount; i++) {
            newEigenwijs.beams[i] = {
              id: generateUUID(),
              adjustable: true,
              height: Math.round(parseInt(searchedHeight ?? "2560") / (2 + i)),
              handleAnchor: true,
            };
          }

          break;
        default:
      }
    }

    /* Updating the eigenwijs object with the new beams. */
    updateEigenwijs(newEigenwijs, "eigenwijs", hasStaanderInUrl);
  };

  // Take the existing beams and calculate the biggest gap between the beams outside the restricted area.
  const calculateNewBeamHeight = (beams: BeamData[], customHeight: number) => {
    let heights = beams.map((item) => item.height);
    heights.push(0, customHeight);
    heights = heights.sort((a, b) => a - b);
    const restricted = beams
      .slice(0, 2)
      .sort((a, b) => a.height - b.height)
      .map((i) => i.height);
    let possibleHeights = [];
    for (let i = 0; i < heights.length - 1; i++) {
      if (heights[i] !== restricted[0]) {
        possibleHeights.push({
          diff: heights[i + 1] - heights[i],
          height: heights[i] + (heights[i + 1] - heights[i]) / 2,
        });
      }
    }
    return Math.floor(
      possibleHeights.sort((a, b) => b.diff - a.diff)[0].height,
    );
  };

  const updateSearchParam = useCallback(
    (param: string, value: string) => {
      searchParams.set(param, value);
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const deleteSearchParam = useCallback(
    (param: string) => {
      searchParams.delete(param);
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const updateSelectedDoor = (door: OptionTemplate) => {
    searchParams.set("door", door.code);
    if (!supportsStaander(state.layout?.code, selectedPreset, door.code)) {
      searchParams.delete("staander");
    }
    setSearchParams(searchParams, { replace: true });
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_SELECTED_DOOR,
      data: door,
    });
  };

  const updateLayout = useCallback(
    (layout: OptionTemplate) => {
      const nextPreset =
        layout.code === "eigenwijs"
          ? state.eigenwijs_layout || defaultEigenwijsLayout
          : undefined;
      searchParams.set("layout", layout.code);
      if (layout.code !== "eigenwijs") {
        searchParams.delete("eigenwijs_layout");
      }
      if (
        !supportsStaander(
          layout.code,
          nextPreset,
          state.selectedDoor?.code ?? searchedDoor,
        )
      ) {
        searchParams.delete("staander");
      }
      setSearchParams(searchParams, { replace: true });
      dispatch({
        type: DoorOptionsReducerTypes.UPDATE_LAYOUT,
        data: layout,
      });
    },
    [
      dispatch,
      searchParams,
      searchedDoor,
      setSearchParams,
      state.eigenwijs_layout,
      state.selectedDoor?.code,
    ],
  );

  //Set layout
  const setLayout = useCallback(() => {
    if (!doorOptions?.layout) {
      return;
    }

    let defaultLayout;
    let foundLayout;

    if (!zakelijkProfielCodes.includes(searchedDoor)) {
      if (zakelijkDoorCodes.includes(searchedDoor)) {
        foundLayout = doorOptions.layout.find(
          (element) => element.code === "geenliggerszakelijk",
        );
      } else {
        defaultLayout = doorOptions.layout.find((element) => element.default);
        foundLayout = doorOptions.layout.find(
          (element) => element.code === searchedLayout,
        );
      }
    } else {
      foundLayout = doorOptions.layout.find(
        (element) => element.code === "e01",
      );
    }

    const restoredLayout = foundLayout ?? defaultLayout;
    if (!restoredLayout) return;

    if (!foundLayout) {
      searchParams.set("layout", restoredLayout.code);
      if (!supportsStaander(restoredLayout.code, undefined, searchedDoor)) {
        searchParams.delete("staander");
      }
      setSearchParams(searchParams, { replace: true });
    }
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_LAYOUT,
      data: restoredLayout,
    });
  }, [
    doorOptions?.layout,
    searchedDoor,
    searchedLayout,
    searchParams,
    setSearchParams,
    dispatch,
  ]);

  const updateColor = useCallback(
    (color: OptionTemplate) => {
      updateSearchParam("color", color.code);
      dispatch({
        type: DoorOptionsReducerTypes.UPDATE_COLOR,
        data: color,
      });
    },
    [dispatch, updateSearchParam],
  );

  const setColor = useCallback(() => {
    if (!doorOptions?.color) {
      return;
    }

    let defaultColor = null;
    let foundColor = null;

    for (const color of doorOptions.color) {
      if (color.default) {
        defaultColor = color;
      }

      if (color.code === searchedColor) {
        foundColor = color;
      }
    }

    if (foundColor) {
      updateColor(foundColor);
      return;
    }

    if (defaultColor) {
      updateColor(defaultColor);
    }
  }, [doorOptions?.color, searchedColor, updateColor]);

  const getConfigurationColor = () => {
    return state.color ? state.color.material?.codeName : "PoedercoatGitZwart";
  };

  const updateGlass = useCallback(
    (glass: OptionTemplate) => {
      updateSearchParam("glass", glass.code);
      dispatch({
        type: DoorOptionsReducerTypes.UPDATE_GLASS,
        data: glass,
      });
    },
    [dispatch, updateSearchParam],
  );

  const setGlass = useCallback(() => {
    if (!doorOptions?.glass) {
      return;
    }

    let defaultGlass;
    let foundGlass;

    if (!zakelijkProfielCodes.includes(searchedDoor)) {
      defaultGlass = doorOptions.glass.find((element) => element.default);
      foundGlass = doorOptions.glass.find(
        (element) => element.code === searchedGlass,
      );
    } else {
      foundGlass = doorOptions.glass.find(
        (element) =>
          element.code === "geenglas" || element.code === "geenglaszakelijk",
      );
    }

    if (foundGlass) {
      updateGlass(foundGlass);
      return;
    }

    if (defaultGlass) {
      updateGlass(defaultGlass);
    }
  }, [doorOptions?.glass, searchedDoor, searchedGlass, updateGlass]);

  const getConfigurationGlass = () => {
    return state.glass ? state.glass.material?.codeName : "Helder";
  };

  const updateHandle = useCallback(
    (handle: OptionTemplate) => {
      updateSearchParam("handle", handle.code);
      dispatch({
        type: DoorOptionsReducerTypes.UPDATE_HANDLE,
        data: handle,
      });
    },
    [dispatch, updateSearchParam],
  );

  const setHandle = useCallback(() => {
    if (!doorOptions?.handle) {
      return;
    }

    if (!initialHandleNormalizationDone.current) {
      initialHandleNormalizationDone.current = true;

      const normalized = normalizeInitialHandle(
        searchedDoor,
        searchedLayout,
        searchedHandle,
        doorOptions.handle,
        getHandleAnchorBeamGap(state.eigenwijs?.beams),
      );

      if (normalized.changed && normalized.nextHandle) {
        updateHandle(normalized.nextHandle);
        return;
      }
    }

    let defaultHandle;
    let foundHandle;

    if (!zakelijkProfielCodes.includes(searchedDoor)) {
      defaultHandle = doorOptions.handle.find(
        (element) => element.type === "handle" && element.default,
      );
      foundHandle = doorOptions.handle.find(
        (element) =>
          element.type === "handle" && element.code === searchedHandle,
      );
    } else {
      foundHandle = doorOptions.handle.find(
        (element) =>
          element.type === "handle" &&
          (element.code === "geengreep" || element.code === "nohandle"),
      );
    }

    if (foundHandle) {
      updateHandle(foundHandle);
      return;
    }

    if (defaultHandle) {
      updateHandle(defaultHandle);
    } else {
      // No handle with default=true  — pick the first usable handle as fallback
      const firstUsable = doorOptions.handle.find(
        (element) => element.type === "handle" && !element.disabled,
      );
      if (firstUsable) {
        updateHandle(firstUsable);
      }
    }
  }, [
    doorOptions?.handle,
    searchedDoor,
    searchedHandle,
    searchedLayout,
    state.eigenwijs?.beams,
    updateHandle,
  ]);

  const updateExtra = useCallback(
    (extra: OptionTemplate[]) => {
      const manualExtras = extra.filter(
        (option) => !isVerticalProfileExtra(option.code),
      );
      if (!manualExtras.length) {
        deleteSearchParam("extra");

        dispatch({
          type: DoorOptionsReducerTypes.UPDATE_EXTRA,
          data: manualExtras,
        });
        return;
      }

      updateSearchParam(
        "extra",
        JSON.stringify(
          manualExtras.map((option) => ({
            id: option.id,
            amount: option.amount ?? 1,
          })),
        ),
      );

      dispatch({
        type: DoorOptionsReducerTypes.UPDATE_EXTRA,
        data: manualExtras,
      });
    },
    [deleteSearchParam, dispatch, updateSearchParam],
  );

  // When changing a color, the extra options update accordingly
  // To not have unwanted extra options as selected, remove the verfset extra options when a color is changing
  const resetVerfsetExtraOptions = () => {
    const extraOptions = state.extra?.filter(
      (extraOption: OptionTemplate) => !extraOption.code.includes("verfset"),
    );

    updateExtra(extraOptions);
    disableKozijnColor();
  };

  const initializeExtraOptionsFromUrl = useCallback(() => {
    if (!doorOptions?.extra) {
      return;
    }

    const searchedExtraOptionsArr: Array<{ id: string; amount?: number }> =
      JSON.parse(decodeURIComponent(searchedExtra || "[]"));

    const checkedOptions: OptionTemplate[] = [];
    searchedExtraOptionsArr.forEach((searchedOption) => {
      const option = doorOptions.extra.find(
        (extraOption) =>
          extraOption.type === "extra" && extraOption.id === searchedOption.id,
      );

      if (option && !isVerticalProfileExtra(option.code)) {
        checkedOptions.push({
          ...option,
          amount: searchedOption.amount ?? 1,
        });
      }
    });

    if (searchedExtraOptionsArr.length !== checkedOptions.length) {
      if (checkedOptions.length) {
        searchParams.set(
          "extra",
          JSON.stringify(
            checkedOptions.map(({ id, amount }) => ({ id, amount })),
          ),
        );
      } else {
        searchParams.delete("extra");
      }
      setSearchParams(searchParams, { replace: true });
    }

    const currentExtras = state.extra ?? [];
    const extrasChanged =
      currentExtras.length !== checkedOptions.length ||
      checkedOptions.some(
        (option, index) =>
          option.id !== currentExtras[index].id ||
          option.amount !== currentExtras[index].amount,
      );
    if (extrasChanged) {
      dispatch({
        type: DoorOptionsReducerTypes.UPDATE_EXTRA,
        data: checkedOptions,
      });
    }
  }, [
    doorOptions?.extra,
    searchParams,
    searchedExtra,
    setSearchParams,
    state.extra,
    dispatch,
  ]);

  useUrlSelectionSync({
    colorOptions: doorOptions?.color,
    currentColorCode: state.color?.code,
    currentGlassCode: state.glass?.code,
    currentHandleCode: state.handle?.code,
    currentLayoutCode: state.layout?.code,
    doorCode: searchedDoor,
    extraOptions: doorOptions?.extra,
    glassOptions: doorOptions?.glass,
    handleOptions: doorOptions?.handle,
    initializeExtraOptionsFromUrl,
    layoutOptions: doorOptions?.layout,
    searchedColor,
    searchedExtra,
    searchedGlass,
    searchedHandle,
    searchedLayout,
    setColor,
    setGlass,
    setHandle,
    setLayout,
  });

  const updateEigenwijs = useCallback(
    (
      eigenwijs: EigenwijsOptions,
      layoutCode = state.layout?.code,
      hasStaander = staanderPosition !== undefined,
    ) => {
      if (eigenwijs.sizeObjects[0] && state.selectedDoor) {
        const firstWidth = eigenwijs.sizeObjects[0].customWidth;
        const equalWidthsRequired = requiresEqualWidths(
          state.selectedDoor.code,
          layoutCode,
          hasStaander,
        );
        const isNewLayout = isNewLayoutCode(layoutCode);
        const standingMinimumWidth = getStandingLayoutMinWidth(
          state.selectedDoor.code,
          hasStaander,
        );
        const minimumWidth =
          standingMinimumWidth ??
          (isNewLayout
            ? newLayoutMinWidth
            : state.selectedDoor.code === "paneelpaneel"
              ? defaultMinPanelWidth
              : defaultMinDoorWidth);
        const maximumWidth =
          AllXLXXLDoorCodes.includes(state.selectedDoor.code) &&
          (!isNewLayout ||
            enabledXLXXLDeMaasDeWaalNewDoors.includes(state.selectedDoor.code))
            ? 1750
            : 1150;
        const normalizeWidth = (width: number) =>
          width < minimumWidth || width > maximumWidth
            ? defaultDoorWidth
            : width;
        const normalizedFirstWidth = normalizeWidth(firstWidth);
        let shouldNormalize = false;
        const sizeObjects =
          equalWidthsRequired || standingMinimumWidth !== undefined
            ? eigenwijs.sizeObjects.map((sizeObject) => {
                const normalizedWidth = equalWidthsRequired
                  ? normalizedFirstWidth
                  : normalizeWidth(sizeObject.customWidth);
                if (normalizedWidth !== sizeObject.customWidth) {
                  shouldNormalize = true;
                }
                return { ...sizeObject, customWidth: normalizedWidth };
              })
            : eigenwijs.sizeObjects;
        const nextEigenwijs = shouldNormalize
          ? { ...eigenwijs, sizeObjects }
          : eigenwijs;

        if (state.layout) {
          updateSearchParam(
            "height",
            nextEigenwijs.sizeObjects[0].customHeight.toString(),
          );

          const widthInUrlForm = encodeURIComponent(
            JSON.stringify(
              nextEigenwijs.sizeObjects.map(
                (sizeObject) => sizeObject.customWidth,
              ),
            ),
          );

          updateSearchParam("width", widthInUrlForm);

          if (layoutCode === "eigenwijs") {
            if (nextEigenwijs.beams.length > 0) {
              const beamInUrlForm = encodeURIComponent(
                JSON.stringify(
                  nextEigenwijs.beams.map((beamData) => beamData.height),
                ),
              );
              updateSearchParam("beam", beamInUrlForm);
            } else {
              deleteSearchParam("beam");
            }
          } else {
            deleteSearchParam("beam");
          }
        }

        dispatch({
          type: DoorOptionsReducerTypes.UPDATE_EIGENWIJS,
          data: nextEigenwijs,
        });
      }
    },
    [
      deleteSearchParam,
      dispatch,
      staanderPosition,
      state.layout,
      state.selectedDoor,
      updateSearchParam,
    ],
  );

  useHandlePolicyRepair({
    beams: state.eigenwijs?.beams,
    currentHandle: state.handle,
    doorCode: state.selectedDoor?.code,
    handleOptions: doorOptions?.handle,
    layoutCode: state.layout?.code,
    pendingHeightRepairNotificationRef,
    updateHandle,
  });

  useDoorHeightNormalization({
    eigenwijs: state.eigenwijs,
    layoutCode: state.layout?.code,
    maxDoorHeight: state.maxDoorHeight,
    minDoorHeight: state.minDoorHeight,
    selectedDoorCode: state.selectedDoor?.code,
    updateEigenwijs,
  });

  const markPendingHeightRepairNotification = () => {
    pendingHeightRepairNotificationRef.current = true;
  };

  const updateBeamHeightValidationError = (isError: boolean) => {
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_SHOW_BEAM_HEIGHT_VALIDATION_ERROR,
      data: isError,
    });
  };

  const getConfigurationBeams = (ignoreAnchors: boolean): BeamData[] => {
    if (!state.eigenwijs) {
      return [];
    }

    return state.eigenwijs.beams.filter((beam: BeamData) => {
      if (ignoreAnchors) {
        return beam;
      } else {
        if (!beam.handleAnchor) return beam;
      }
    });
  };

  const getConfigurationAnchorBeams = (): BeamData[] => {
    if (!state.eigenwijs) {
      return [];
    }

    return state.eigenwijs.beams.filter((beam: BeamData) => {
      if (beam.handleAnchor) {
        return beam;
      }
    });
  };

  const getConfigurationHeight = (index: number) => {
    return state.eigenwijs?.sizeObjects[index].customHeight
      ? state.eigenwijs?.sizeObjects[index].customHeight
      : defaultDoorHeight;
  };

  const getConfigurationWidth = (index: number) => {
    return state.eigenwijs?.sizeObjects[index].customWidth
      ? state.eigenwijs?.sizeObjects[index].customWidth
      : defaultDoorWidth;
  };

  const updateMinDoorWidth = (width: number) => {
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_MIN_DOOR_WIDTH,
      data: width,
    });
  };

  const updateMaxDoorWidth = (width: number) => {
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_MAX_DOOR_WIDTH,
      data: width,
    });
  };

  const updateMinDoorHeight = (height: number) => {
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_MIN_DOOR_HEIGHT,
      data: height,
    });
  };

  const updateMaxDoorHeight = (height: number) => {
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_MAX_DOOR_HEIGHT,
      data: height,
    });
  };

  const hasXLDoorOption = (extraOptions: OptionTemplate[]) => {
    return extraOptions.some((item: OptionTemplate) =>
      item.code.includes(doorCodeXL),
    );
  };

  const hasXXLDoorOption = (extraOptions: OptionTemplate[]) => {
    return extraOptions.some((item: OptionTemplate) =>
      item.code.includes(doorCodeXXL),
    );
  };

  const getConfigurationPositions = (
    defaultPositions: Vector3[],
  ): Vector3[] => {
    const alteredPositions = defaultPositions;
    if (state.eigenwijs) {
      state.eigenwijs.sizeObjects.forEach(
        (value: SizeObject, index: number) => {
          const widthPercentage = value.customWidth / defaultDoorWidth;
          const offset =
            Math.abs(defaultDoorWidth - value.customWidth) * 0.00047;
          let x = alteredPositions[index].x;

          if (widthPercentage > 1) {
            x = x > 0 ? x + offset : x - offset;
          } else if (widthPercentage < 1) {
            x = x > 0 ? x - offset : x + offset;
          }

          alteredPositions[index] = new Vector3(
            x,
            alteredPositions[index].y,
            alteredPositions[index].z,
          );

          if (alteredPositions.length > 2 && index >= 1) {
            for (let i = index; i < alteredPositions.length; i++) {
              const oneMoreActionRequired = i + 1 < alteredPositions.length;
              if (oneMoreActionRequired) {
                if (widthPercentage > 1) {
                  alteredPositions[i + 1].x += offset * 2;
                } else if (widthPercentage < 1) {
                  alteredPositions[i + 1].x -= offset * 2;
                }
              }
            }
          }
        },
      );
    }

    return alteredPositions;
  };

  const isExtraChecked = (
    extra: OptionTemplate[] | null,
    item: OptionTemplate,
  ): boolean => {
    if (!extra) return false;
    return extra.find((option) => option.id === item.id) !== undefined;
  };

  const getConfiguredOptionPricingInput =
    useCallback((): ConfiguredOptionPricingInput => {
      return {
        selectedDoor: state.selectedDoor,
        layout: state.layout,
        color: state.color,
        glass: state.glass,
        handle: state.handle,
        extra: selectedExtras,
      };
    }, [
      state.color,
      selectedExtras,
      state.glass,
      state.handle,
      state.layout,
      state.selectedDoor,
    ]);

  const getConfiguredOptionPrices = useCallback((): ConfiguredOptionPrices => {
    return computeConfiguredOptionPrices(getConfiguredOptionPricingInput());
  }, [getConfiguredOptionPricingInput]);

  const calculateExtrasPrice = () => {
    return getConfiguredOptionPrices().extras;
  };

  const getConfigurationPrice = () => {
    let price = 0;
    const configuredOptionPrices = getConfiguredOptionPrices();

    if (state.selectedDoor) {
      state.selectedDoor.calculatedPrice = state.selectedDoor.price;
      price += state.selectedDoor.price;

      if (state.layout) {
        state.layout.calculatedPrice = configuredOptionPrices.layout;
        price += configuredOptionPrices.layout;
      }
      if (state.color) {
        state.color.calculatedPrice = configuredOptionPrices.color;
        price += state.color.calculatedPrice;
      }

      if (state.glass) {
        state.glass.calculatedPrice = configuredOptionPrices.glass;
        price += state.glass.calculatedPrice;
      }

      if (state.handle) {
        state.handle.calculatedPrice = configuredOptionPrices.handle;
        price += configuredOptionPrices.handle;
      }

      if (selectedExtras.length > 0) {
        price += configuredOptionPrices.extras;
      }

      if (
        state.selfAssembleDoorData.selfAssembly &&
        state.selectedDoor.selfAssemblyDiscount
      ) {
        price -= state.selectedDoor.selfAssemblyDiscount;
      }
    }

    return price;
  };

  const updateSelfAssembleData = (data: SelfAssembleFields) => {
    updateSearchParam("isSelfAssembly", data.selfAssembly.toString());
    dispatch({
      type: DoorOptionsReducerTypes.UPDATE_SELF_ASSEMBLE_DOOR_DATA,
      data,
    });
  };

  const getDoorProduct = () => {
    const configuredOptionPrices = getConfiguredOptionPrices();
    const extras: ExtraOptionScheme[] = selectedExtras.map(
      ({ id, code, name, price, amount }) => ({
        id,
        amount: amount || 1,
        name: getExtraOptionDisplayName(code, name, staanderPosition),
        price,
      }),
    );

    const doorName = state.selectedDoor ? state.selectedDoor.name : "-";
    const doorPrice = "€" + (state.selectedDoor.price ?? 0).toFixed(2);

    const doorLayoutName = state.layout ? state.layout.name : "";
    const doorLayoutPrice = "€" + configuredOptionPrices.layout.toFixed(2);
    const doorColorName = state.color ? state.color.name : "-";
    const doorColorPrice = "€" + configuredOptionPrices.color.toFixed(2);

    const doorHandleName = state.handle ? state.handle.name : "-";
    const doorHandlePrice = "€" + configuredOptionPrices.handle.toFixed(2);

    const doorGlassName = state.glass ? state.glass.name : "-";
    const doorGlassPrice = "€" + configuredOptionPrices.glass.toFixed(2);

    const montageText = state.selectedDoor.isSelfAssembly
      ? "Zelf monteren"
      : "Montage door GewoonGers";

    const zelfMontagePrice =
      "€" + (state.selectedDoor.selfAssemblyDiscount ?? 0).toFixed(2);

    const isSelfAssembly = state.selectedDoor.isSelfAssembly;

    const doorProduct = {
      totalPrice: getConfigurationPrice(),
      doorName: doorName,
      doorPrice: doorPrice,
      doorLayoutName: doorLayoutName,
      doorLayoutPrice: doorLayoutPrice,
      doorColorName: doorColorName,
      doorColorPrice: doorColorPrice,
      doorHandleName: doorHandleName,
      doorHandlePrice: doorHandlePrice,
      doorGlassName: doorGlassName,
      doorGlassPrice: doorGlassPrice,
      montageText: montageText,
      zelfMontagePrice: zelfMontagePrice,
      isSelfAssembly: isSelfAssembly,
      extraOptions: extras,
    };

    return doorProduct;
  };

  const getConfigurationProduct = (): Product | null => {
    if (!state.selectedDoor) return null;
    if (!state.layout) return null;
    if (!state.color) return null;
    if (!state.glass) return null;
    if (!state.handle) return null;

    const configuredOptionPrices = getConfiguredOptionPrices();

    let beams: number[] = [];
    let height: number = 0;
    let widths: number[] = [];

    if (state.eigenwijs) {
      for (let i = 0; i < state.eigenwijs.beams.length; i++) {
        beams.push(state.eigenwijs.beams[i].height);
      }

      for (let i = 0; i < state.eigenwijs.sizeObjects.length; i++) {
        widths.push(state.eigenwijs.sizeObjects[i].customWidth);
      }

      height = state.eigenwijs.sizeObjects[0].customHeight;
    }

    const extras: ExtraOptionScheme[] = [];
    const extrasNames: string[] = [];
    const extrasPrices: number[] = [];
    selectedExtras.forEach(({ id, code, name, price, amount }) => {
      extras.push({ id, amount: amount || 1, name, price });
      extrasNames.push(getExtraOptionDisplayName(code, name, staanderPosition));
      if (zakelijkDoorCodes.includes(state.selectedDoor.code)) {
        extrasPrices.push(price * (amount || 1));
      }
    });

    const configuredProduct: ConfiguredProduct = {
      totalPrice: getConfigurationPrice(),
      doorColor: {
        id: state.color.id,
        name: state.color.name,
        code: state.color.code,
        description: state.color.description,
        imageId: state.color.imageId,
        price: configuredOptionPrices.color,
      },
      glassType: {
        id: state.glass.id,
        name: state.glass.name,
        code: state.glass.code,
        description: state.glass.description,
        imageId: state.glass.imageId,
        price: configuredOptionPrices.glass,
      },
      doorHandle: {
        id: state.handle.id,
        name: state.handle.name,
        code: state.handle.code,
        description: state.handle.description,
        imageId: state.handle.imageId,
        price: configuredOptionPrices.handle,
      },
      doorType: {
        id: state.selectedDoor.id,
        name: state.selectedDoor.name,
        code: state.selectedDoor.code,
        description: state.selectedDoor.description,
        imageId: state.selectedDoor.imageId,
        price: state.selectedDoor.price,
      },
      doorLayout: {
        id: state.layout.id,
        name: state.layout.name,
        code: state.layout.code,
        description: state.layout.description,
        imageId: state.layout.imageId,
        price: configuredOptionPrices.layout,
      },
      extraOptions: extras,
      extraOptionsNames: extrasNames,
      extraOptionsPrices: extrasPrices,
      measurements: {
        height: height,
        widths: widths,
        beams: beams,
        mirrored: false,
        ...(staanderPosition
          ? {
              verticalBeams: getStaanderVerticalBeams(
                widths,
                state.selectedDoor.code,
                staanderPosition,
              ),
            }
          : {}),
      },
      preset: "",
    };

    if (state.selfAssembleDoorData.selfAssembly) {
      configuredProduct.label = state.selfAssembleDoorData.label;
      configuredProduct.isSelfAssembly =
        state.selfAssembleDoorData.selfAssembly;
      configuredProduct.measurements = {
        ...configuredProduct.measurements,
        ...state.selfAssembleDoorData.measurements,
      };
      configuredProduct.selfAssemblyDiscount =
        state.selectedDoor.selfAssemblyDiscount;
    }

    if (state.eigenwijs_layout) {
      configuredProduct.eigenwijsLayout = {
        code: state.eigenwijs_layout,
      };
    }

    return configuredProduct;
  };

  // Layouts of the selected door
  const layoutList = useMemo(
    () => (doorOptions ? [...doorOptions.layout] : []),
    [doorOptions],
  );

  // Main variant multiple n doors: "dewaaldubbel"
  // Main variant opdek/stompe: "debeursdewaal"
  const mainDeWaal = useMemo(() => {
    return findMainVariant(layoutList, deWaalCodes, [
      "dewaaldubbel",
      "dewaal",
      "debeursdewaal",
    ]);
  }, [layoutList]);

  // Main variant multiple n doors: "demaasdubbel"
  // Main variant opdek/stompe: "debeursdemaas"
  const mainDeMaas = useMemo(() => {
    return findMainVariant(layoutList, deMaasCodes, [
      "demaasdubbel",
      "demaas",
      "debeursdemaas",
    ]);
  }, [layoutList]);

  const mainDeIjssel = useMemo(() => {
    return findMainVariant(layoutList, deIjsselCodes, [
      "deijsseldubbel",
      "deijssel",
    ]);
  }, [layoutList]);

  const splitLayouts = (layouts: OptionTemplate[]) => {
    const layoutResult = layouts.reduce(
      (acc, item) => {
        const code = item.code;
        const group = getClassicLayoutGroup(code);
        if (group) {
          acc.classicLayouts[group].push(item);
          acc.sortedLayoutList.push(item);
          return acc;
        }

        if (isDeWaalCode(code)) {
          if (code === mainDeWaal?.code) {
            acc.sortedLayoutList.push(item);
          } else {
            acc.dewaalVariants.push(item);
          }
          return acc;
        }

        if (isDeMaasCode(code)) {
          if (code === mainDeMaas?.code) {
            acc.sortedLayoutList.push(item);
          } else {
            acc.deMaasVariants.push(item);
          }
          return acc;
        }

        if (isDeIjsselCode(code)) {
          if (code === mainDeIjssel?.code) {
            acc.sortedLayoutList.push(item);
          } else {
            acc.deIjsselVariants.push(item);
          }
          return acc;
        }

        acc.sortedLayoutList.push(item);
        return acc;
      },
      {
        sortedLayoutList: [] as OptionTemplate[],
        classicLayouts: {
          dekaap: [] as OptionTemplate[],
          deblaak: [] as OptionTemplate[],
          demeent: [] as OptionTemplate[],
        },
        dewaalVariants: [] as OptionTemplate[],
        deMaasVariants: [] as OptionTemplate[],
        deIjsselVariants: [] as OptionTemplate[],
      },
    );

    // Also include main layout in variant groups
    if (mainDeWaal) layoutResult.dewaalVariants.unshift(mainDeWaal);
    if (mainDeMaas) layoutResult.deMaasVariants.unshift(mainDeMaas);
    if (mainDeIjssel) layoutResult.deIjsselVariants.unshift(mainDeIjssel);

    return layoutResult;
  };

  // DeWaal variants separated from other layouts
  const {
    classicLayouts,
    dewaalVariants,
    deMaasVariants,
    deIjsselVariants,
    sortedLayoutList,
  } = splitLayouts(layoutList);

  // Is self assembly selected from URL
  const isSelfAssembly = searchedIsSelfAssembly === "true";

  // Is De Meent height change applicable
  const layoutCode = state.layout?.code;
  const isDoubleJHandle = state.handle?.code.includes(
    "normalhandletaatsdubbel",
  );

  // Is double handle chosen
  const isDoubleHandleChosen =
    state.handle?.code.includes("verticalhandletaatsschuifdubbel") ||
    isDoubleJHandle;

  const isNewLayout = isNewLayoutCode(layoutCode);
  const isDeMaasLayout = isDeMaasCode(layoutCode);
  const isDeMaas2DubbelLayout = layoutCode === "demaas2dubbel";
  const isDeWaalLayout = isDeWaalCode(layoutCode);
  const isDeIjsselLayout = isDeIjsselCode(layoutCode);
  const isCutLayout = isCutLayoutCode(layoutCode);

  const isNotMaasWaalIjsselLayout =
    !isDeMaasLayout && !isDeWaalLayout && !isDeIjsselLayout;

  // Layout options of a door
  // Used for profiles in this context
  const doorLayoutOptions = doorOptions?.layout || [];

  // Determine if its an XL or XXL scene
  const doorCurrentWidth = getConfigurationWidth(0);
  const isXLXXLScene = doorCurrentWidth >= 1151;

  //Logic to rotate De Waal doors if needed
  const doorNumber = state.selectedDoor?.configuration.nElements
    ? state.selectedDoor.configuration.nElements
    : 1;

  const isDeWaal34Door = isDeWaalLayout && doorNumber > 2;
  //Only rotate De Waal layout if more than 2 door combos and dewaal2,3 layout is selected
  const rotateDeWaal23 =
    isDeWaal34Door &&
    (layoutCode === "dewaal2dubbel" || layoutCode === "dewaal3dubbel");

  // Only rotate De Waal 4 layout if more than 2 door combos and dewaal4 layout is selected
  const rotateDeWaal4 = isDeWaal34Door && layoutCode === "dewaal4dubbel";

  const rotateDeMaas2Dubbel = isDeMaasLayout && layoutCode === "demaas2dubbel";

  const lengthRule = isOpdekOrStomp ? 0 : 1;
  const selectedGroup = getLayoutBrowseGroup(layoutCode);
  const isClassicLayoutGroup = getClassicLayoutGroup(layoutCode) !== null;
  const browseGroup =
    layoutNavigation &&
    layoutNavigation.doorCode === state.selectedDoor?.code &&
    layoutNavigation.layoutCode === layoutCode
      ? layoutNavigation.group
      : isClassicLayoutGroup && layoutCode === selectedGroup
        ? null
        : selectedGroup;
  const layoutGroups = {
    ...classicLayouts,
    dewaal: dewaalVariants,
    demaas: deMaasVariants,
    deijssel: deIjsselVariants,
  };
  const getLayoutSubchoiceCount = (code: string): number => {
    if (code === "eigenwijs") return 3;

    const group = getLayoutBrowseGroup(code);
    if (!group) return 0;

    if (getClassicLayoutGroup(code)) {
      const hasProfileChoices =
        !!profileOption &&
        supportsStaander(code, undefined, state.selectedDoor?.code) &&
        layoutGroups[group].length > 0;

      return hasProfileChoices ? staanderPositions.length : 0;
    }

    return layoutGroups[group].length > lengthRule
      ? layoutGroups[group].length
      : 0;
  };
  const visibleGroup = browseGroup ? layoutGroups[browseGroup] : null;
  const groupedLayouts =
    visibleGroup && layoutCode && getLayoutSubchoiceCount(layoutCode) > 0
      ? visibleGroup
      : null;
  const openLayoutGroup = (code: string) =>
    setLayoutNavigation({
      doorCode: state.selectedDoor?.code,
      layoutCode: code,
      group: getLayoutBrowseGroup(code),
    });
  const closeLayoutGroup = () =>
    setLayoutNavigation({
      doorCode: state.selectedDoor?.code,
      layoutCode,
      group: null,
    });

  // Decide if the layout is one of the new round layouts (de waal family)
  const isLeafStyleDeWaalLayout = isDeWaalLeafStyleLayoutCode(layoutCode);

  return (
    <ConfigurationOptionsContext.Provider
      value={{
        ...state,

        staanderPosition,
        sceneStaanderPosition,
        staanderOption,

        doorOptions,
        doorOptionsLoadError,
        doorOptionsLoading,
        sortedLayoutList,
        dewaalVariants,
        deMaasVariants,
        groupedLayouts,
        getLayoutSubchoiceCount,
        openLayoutGroup,
        closeLayoutGroup,

        updateSelectedDoor,
        updateLayout,
        updateColor,
        updateColoredGlass,
        updateGlass,
        updateHandle,

        updateExtra,
        updateEigenwijs,
        markPendingHeightRepairNotification,
        updateEigenwijsLayout,
        updateStaander,
        updateSearchParam,

        calculateNewBeamHeight,
        updateAnchorBeams,

        getDefaultLayoutBeamData,

        updateBeamHeightValidationError,
        updateSelfAssembleData,

        getConfigurationBeams,
        getConfigurationAnchorBeams,
        getConfigurationHeight,
        getConfigurationWidth,
        getConfigurationPositions,
        getConfigurationColor,
        getConfigurationGlass,
        getConfiguredOptionPrices,
        getConfigurationPrice,
        getConfigurationProduct,
        getDoorProduct,

        hasXLDoorOption,
        hasXXLDoorOption,

        resetVerfsetExtraOptions,
        isExtraChecked,
        isSelfAssembly,
        isDoubleJHandle,
        isDoubleHandleChosen,

        isDeMaasLayout,
        isDeMaas2DubbelLayout,
        isDeWaalLayout,
        isDeIjsselLayout,
        isNotMaasWaalIjsselLayout,
        isCutLayout,
        mainDeWaal,
        mainDeMaas,

        updateMaxDoorWidth,
        doorLayoutOptions,
        calculateExtrasPrice,

        isXLXXLScene,
        deMeentHeightChangeApplies,
        rotateDeWaal23,
        rotateDeWaal4,
        rotateDeMaas2Dubbel,
        isOpdekOrStomp,

        isNewLayout,
        isSingleMaxHeightDoor,
        isLeafStyleDeWaalLayout,
      }}
    >
      {children}
    </ConfigurationOptionsContext.Provider>
  );
};
export default ConfigurationOptionsProvider;
